import { friendlyAttrLabel } from './labels';

export interface NotificationObject {
  contextName?: string;
  action?: string;
  ruleTagName?: string;
  recvTime?: string;
  read?: boolean;
  trend?: 'up' | 'down';
  attr?: string;
  attributeKey?: string;
  user?: string;
  confidence?: 'high' | 'medium';
  locationAccessed?: boolean;
  locationVerified?: boolean;
}

export type NotificationsResponse = Array<NotificationObject>;

// Función para formatear la fecha en formato local
const formatDateString = (dateString?: string): string => {
  if (!dateString) {
      console.error('Fecha indefinida o nula.');
      return '';
  }

  try {
      // Crear un objeto Date a partir de la cadena de fecha en formato UTC
      const utcDate = new Date(dateString); // Asegurarse de que se trata como UTC

      // Verificar si la fecha es válida
      if (isNaN(utcDate.getTime())) {
          console.error('Formato de fecha no válido:', dateString);
          return '';
      }

      // Convertir automáticamente a la zona horaria local y formatear manualmente
      const year = utcDate.getFullYear();
      const month = String(utcDate.getMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getDate()).padStart(2, '0');
      const hours = String(utcDate.getHours()).padStart(2, '0');  // Esto usa la hora local
      const minutes = String(utcDate.getMinutes()).padStart(2, '0');
      const seconds = String(utcDate.getSeconds()).padStart(2, '0');

      // Construir la fecha en formato local similar a ISO 8601 (con la "T" y sin zona horaria)
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  } catch (error) {
      console.error('Error al formatear la fecha:', error);
      return '';
  }
};

// Función para obtener el atributo real a partir de ruleTagName (mapea nombres amigables a campos reales)
const getAttrFromRuleTagName = (ruleTagName: string): string => {
  const base = ruleTagName.replace(/Umbral$/i, '');
  // Mapeos conocidos del backend
  if (base === 'voltajeBateria' || base === 'voltaje_bateria') return 'voltaje_device';
  return base;
};

// Implementación de makeNotification que utiliza formatDateString
export const makeNotification = (event: any): NotificationObject | undefined => {
  if (typeof event === "string") return undefined;
  try {
    const recvTime: string | undefined = event.recvTime;
    const localDate = formatDateString(recvTime);

    const rt = String(event.ruleTagName || '').toLowerCase();

    // Helper: normalizar flanco
    const flankStr = String(event.flanco || '').toLowerCase();
    const trend: 'up' | 'down' | undefined =
      flankStr === 'subida' || flankStr === 'arriba' ? 'up'
      : flankStr === 'bajada' || flankStr === 'abajo' ? 'down'
      : undefined;

    // 1) Staleness alerts (no flanco)
    if (rt === 'staleness' || event.status === 'stale' || (event.status === 'recovered' && (event.silenceSecs !== undefined || event.ruleTagName === 'staleness'))) {
      const status = String(event.status || '').toLowerCase();
      const attr = 'staleness';
      const action = status === 'stale'
        ? `Sin datos ~${event.silenceSecs ?? ''}s`
        : 'Recuperado';
      return {
        contextName: event.contextName || 'Contexto desconocido',
        action,
        ruleTagName: 'staleness',
        recvTime: localDate,
        read: false,
        trend: status === 'stale' ? 'down' : 'up',
        attr: friendlyAttrLabel(attr),
        user: event.user,
      };
    }

    // 1b) Freshness alerts (llega una carga manual)
    if (rt === 'freshness') {
      return {
        contextName: event.contextName || 'Contexto desconocido',
        action: event.action || 'Nueva actualización de datos',
        ruleTagName: 'freshness',
        recvTime: localDate,
        read: false,
        trend: 'up',
        attr: friendlyAttrLabel('freshness'),
        user: event.user,
      };
    }

    // 1c) Stuck value alerts
    if (rt === 'stuckvalue' || event.status === 'stuck' || (event.status === 'recovered' && (event.stuckCount !== undefined || event.ruleTagName === 'stuckValue'))) {
      const status = String(event.status || '').toLowerCase();
      const attr = String(event.attribute || event.metricId || 'stuckValue');
      const action = status === 'recovered'
        ? 'Recuperado'
        : `Valor congelado (${event.consecutiveCount ?? ''}/${event.stuckCount ?? ''})`;
      return {
        contextName: event.contextName || 'Contexto desconocido',
        action,
        ruleTagName: 'stuckValue',
        recvTime: localDate,
        read: false,
        trend: status === 'recovered' ? 'up' : 'down',
        attr: friendlyAttrLabel(attr),
        attributeKey: attr,
        user: event.user,
      };
    }
    // Community observations are delivered through the regular alert WebSocket.
    if (rt === 'communityobservation' || event.event_type === 'community_observation') {
      const questionLabels: Record<string, string> = {
        sand_litter: 'Basura en la arena',
        green_patches: 'Posibles cianobacterias',
        foam_presence: 'Espuma en la orilla',
        unusual_odor: 'Olor inusual',
        jellyfish_presence: 'Medusas',
        possible_bioluminescence: 'Bioluminiscencia',
        dead_animal_sighting: 'Animales muertos',
        crowding: 'Concurrencia',
      };
      const answerLabels: Record<string, string> = {
        yes: 'Sí',
        no: 'No',
        not_sure: 'No estoy seguro',
        cannot_observe: 'No pude observarlo',
        none: 'Nada',
        little: 'Poca',
        some: 'Bastante',
        much: 'Mucha',
        almost_empty: 'Casi vacía',
        few_people: 'Poca gente',
        some_people: 'Bastante gente',
        very_crowded: 'Muy concurrida',
      };
      const questionId = String(event.community_question_id || event.question_id || '');
      const question = questionLabels[questionId] || 'Observación comunitaria';
      const answerKey = String(event.community_answer || event.answer || '');
      const answer = answerLabels[answerKey] || answerKey || 'Respuesta registrada';
      return {
        contextName: event.contextName || 'Contexto desconocido',
        action: `${question}: ${answer}`,
        ruleTagName: 'communityObservation',
        recvTime: localDate,
        read: false,
        trend: 'up',
        attr: 'Observación comunitaria',
        confidence: event.confidence === 'high' ? 'high' : 'medium',
        locationAccessed: event.locationAccessed === true || event.location_accessed === true,
        locationVerified: event.locationVerified === true || event.location_verified === true,
        user: event.user,
      };
    }


    // 2) Median band alerts
    if (rt === 'medianband' || (event.attribute && event.median !== undefined && event.epsilon !== undefined)) {
      const attr = String(event.attribute);
      const val = event.value;
      const bandMin = event.bandMin;
      const bandMax = event.bandMax;
      const action = `${attr}=${val} fuera [${bandMin},${bandMax}]`;
      return {
        contextName: event.contextName || 'Contexto desconocido',
        action,
        ruleTagName: 'medianBand',
        recvTime: localDate,
        read: false,
        trend: trend || 'up',
        attr: friendlyAttrLabel(attr),
        attributeKey: attr,
        user: event.user,
      };
    }

    // 3) Rate-of-Change alerts
    if (rt === 'rateofchange' || (event.attribute && event.delta !== undefined && event.dtSec !== undefined)) {
      const attr = String(event.attribute);
      const dv = event.delta;
      const dt = event.dtSec;
      const dps = event.deltaPerSec;
      const mode = event.mode ? ` (${event.mode})` : '';
      const action = `Δ=${dv} en ${dt}s (${dps}/s)${mode}`;
      return {
        contextName: event.contextName || 'Contexto desconocido',
        action,
        ruleTagName: 'rateOfChange',
        recvTime: localDate,
        read: false,
        trend: trend || (dv >= 0 ? 'up' : 'down'),
        attr: friendlyAttrLabel(attr),
        attributeKey: attr,
        user: event.user,
      };
    }

    // 4) Generic threshold alerts
    if (event.attribute && event.threshold !== undefined && event.value !== undefined) {
      const attr = String(event.attribute);
      const value = event.value;
      const status = String(event.status || '').toLowerCase();
      const action = status === 'recovered'
        ? `${attr} recuperado en ${value}`
        : `${attr}=${value} (umbral ${event.operator === 'lte' ? '≤' : '≥'} ${event.threshold})`;
      return {
        contextName: event.contextName || 'Contexto desconocido',
        action,
        ruleTagName: event.ruleTagName || 'threshold',
        recvTime: localDate,
        read: false,
        trend: status === 'recovered' ? 'up' : trend,
        attr: friendlyAttrLabel(attr),
        attributeKey: attr,
        user: event.user,
      };
    }

    // 4) Fallback: threshold-style alerts (regla "Umbral")
    const attrLabel = String(event.ruleTagName || '').replace(/Umbral$/i, '');
    const attrKey = getAttrFromRuleTagName(String(event.ruleTagName || ''));
    // Intentar varias fuentes de valor basadas en el atributo real
    let value = event[attrKey];
    let foundKey: string | undefined = undefined;
    if (value === undefined) {
      if (attrKey === 'voltaje_device' && event['voltajeDevice'] !== undefined) value = event['voltajeDevice'];
      if (attrKey === 'temperatura' && event['temperatura'] !== undefined) value = event['temperatura'];
      if (attrKey === 'conductividad' && event['conductividad'] !== undefined) value = event['conductividad'];
    }
    // Si aún está indefinido, buscar el primer campo numérico plausible del evento
    if (value === undefined) {
      const exclude = new Set(['user','ruleTagName','recvTime','timestamp','flanco','contextName','attribute','median','epsilon','bandMin','bandMax','value','prevValue','delta','dtSec','deltaPerSec','status']);
      const keys = Object.keys(event || {});
      for (const k of keys) {
        if (exclude.has(k)) continue;
        const v = (event as any)[k];
        if (typeof v === 'number' && Number.isFinite(v)) {
          value = v;
          foundKey = k;
          break;
        }
      }
    }
    if ((trend === 'up' || trend === 'down') && value !== undefined) {
      return {
        contextName: event.contextName || 'Contexto desconocido',
        action: String(value),
        ruleTagName: event.ruleTagName,
        recvTime: localDate,
        read: false,
        trend,
        attr: friendlyAttrLabel(attrLabel || foundKey || attrKey),
        attributeKey: foundKey || attrKey,
        user: event.user,
      };
    }
  } catch (error: any) {
    console.error(`[ERROR][MAKENOTIFICATION] al procesar el evento: ${error.message}`);
  }
  return undefined;
};
